import type { AxiosInstance, AxiosProxyConfig, AxiosRequestConfig, ResponseType } from 'axios';
import type { HttpMethod } from '~/types/network';

import { API_PREFIX, SERVICES_PREFIXES } from '~/const/RequestServices/RequestServicesConst';
import { TokenStorage } from '~/services/TokenStorage';

interface RequestOptions {
  proxy?: AxiosProxyConfig;
  url: string;
  baseURL?: string;
  data?: any;
  method?: HttpMethod;
  params?: any;
  cache?: boolean;
  headers?: Record<string, any>;
  auth?: any;
  responseType?: ResponseType;
  requiresToken?: boolean;

  /** Не открывать модалку входа для ожидаемого 401, например внутри формы логина. */
  showAuthOnUnauthorized?: boolean;

  /** Внутренний флаг: запрос уже повторялся после refresh. */
  _retryToken?: boolean;
}

type TokenType = 'access' | 'guest';

type ValidToken = {
  token: string;
  type: TokenType;
};

type RefreshedToken = {
  token: string;
  expiration: number;
};

export const ERROR_NOT_FOUND = {
  HTTP_CODE: 404,
  MESSAGE: 'Not Found',
};

export const isErrorNotFoundResponse = (error: any): boolean =>
  error?.response?.status === ERROR_NOT_FOUND.HTTP_CODE;

const isSessionRejected = (error: any): boolean => error?.response?.status === 401;

export class RequestService {
  /** Single-flight: параллельные запросы ждут один и тот же refresh. */
  private accessRefreshPromise: Promise<RefreshedToken> | null = null;
  private guestRefreshPromise: Promise<RefreshedToken> | null = null;
  private guestTokenPromise: Promise<RefreshedToken> | null = null;

  /** Не даёт серии параллельных 401 открыть много одинаковых модалок. */
  private authRequiredEmitted = false;

  constructor(private axios: AxiosInstance) {}

  public async request(options: RequestOptions): Promise<any> {
    const opts: RequestOptions = {
      ...options,
      method: options.method ?? 'GET',
      params: options.params ?? {},
      headers: { ...(options.headers ?? {}) },
      showAuthOnUnauthorized: options.showAuthOnUnauthorized ?? true,
    };

    let usedTokenType: TokenType | null = null;

    if (opts.requiresToken) {
      const tokenData = await this.getValidToken(opts.showAuthOnUnauthorized);
      usedTokenType = tokenData.type;
      this.applyTokenHeader(opts.headers!, tokenData);
    }

    try {
      const response = await this.axios.request(opts as AxiosRequestConfig);

      if (usedTokenType === 'access') {
        this.authRequiredEmitted = false;
      }

      return response;
    } catch (error: any) {
      if (isErrorNotFoundResponse(error)) {
        throw new Error(ERROR_NOT_FOUND.MESSAGE);
      }

      const isUnauthorized = error?.response?.status === 401;

      if (!opts.requiresToken || !isUnauthorized || opts._retryToken || !usedTokenType) {
        throw error;
      }

      let refreshedToken: RefreshedToken;
      try {
        refreshedToken =
          usedTokenType === 'access'
            ? await this.refreshAccessToken()
            : await this.refreshGuestToken();
      } catch (refreshError) {
        // Временная недоступность сети/backend не означает потерю сессии.
        if (!isSessionRejected(refreshError)) throw refreshError;
        if (usedTokenType === 'access') {
          this.handleLostAccessSession(opts.showAuthOnUnauthorized);
          throw refreshError;
        } else {
          /*
           * Если refresh гостевого токена не принят, создаём нового гостя.
           * Это не требует показа формы авторизации.
           */
          refreshedToken = await this.fetchNewGuestToken(true);
        }
      }

      const retryHeaders = { ...(opts.headers ?? {}) };
      this.applyTokenHeader(retryHeaders, { token: refreshedToken.token, type: usedTokenType });
      try {
        return await this.axios.request({
          ...(opts as AxiosRequestConfig),
          headers: retryHeaders,
          _retryToken: true,
        } as AxiosRequestConfig);
      } catch (retryError) {
        if (usedTokenType === 'access' && isSessionRejected(retryError)) {
          this.handleLostAccessSession(opts.showAuthOnUnauthorized);
        }
        throw retryError;
      }
    }
  }

  /** Возвращает действующий guest-token, обновляя или создавая его при необходимости. */
  public async ensureGuestToken(): Promise<RefreshedToken> {
    const state = TokenStorage.getGuestTokenState();

    if (state.status === 'valid') {
      return { token: state.token, expiration: state.expiration };
    }

    if (state.status === 'refresh-required' || state.status === 'expired') {
      try {
        return await this.refreshGuestToken(state.token);
      } catch (error) {
        if (!isSessionRejected(error)) throw error;
        return await this.fetchNewGuestToken(true);
      }
    }

    return await this.fetchNewGuestToken();
  }

  /** Публичный принудительный refresh access-token. */
  public async refreshAccessToken(currentToken?: string): Promise<RefreshedToken> {
    if (!import.meta.client) {
      throw new Error('Access token refresh is available only on client side');
    }

    if (this.accessRefreshPromise) {
      return await this.accessRefreshPromise;
    }

    const state = TokenStorage.getAccessTokenState();
    const token = currentToken ?? (state.status === 'missing' ? null : state.token);

    if (!token) {
      throw new Error('Access token is missing');
    }

    if (state.status !== 'missing' && !state.canRefresh) {
      throw new Error('Access refresh window has expired');
    }

    this.accessRefreshPromise = this.requestTokenRefresh(
      `${API_PREFIX}/${SERVICES_PREFIXES.refreshAccessToken}`,
      'x-access-token',
      token,
      'access'
    );

    try {
      return await this.accessRefreshPromise;
    } finally {
      this.accessRefreshPromise = null;
    }
  }

  /** Публичный принудительный refresh guest-token. */
  public async refreshGuestToken(currentToken?: string): Promise<RefreshedToken> {
    if (!import.meta.client) {
      throw new Error('Guest token refresh is available only on client side');
    }

    if (this.guestRefreshPromise) {
      return await this.guestRefreshPromise;
    }

    const state = TokenStorage.getGuestTokenState();
    const token = currentToken ?? (state.status === 'missing' ? null : state.token);

    if (!token) {
      return await this.fetchNewGuestToken();
    }

    this.guestRefreshPromise = this.requestTokenRefresh(
      `${API_PREFIX}/${SERVICES_PREFIXES.refreshGuestToken}`,
      'x-guest-token',
      token,
      'guest'
    );

    try {
      return await this.guestRefreshPromise;
    } finally {
      this.guestRefreshPromise = null;
    }
  }

  private async getValidToken(showAuthOnUnauthorized = true): Promise<ValidToken> {
    const accessState = TokenStorage.getAccessTokenState();

    if (accessState.status === 'valid') {
      return { token: accessState.token, type: 'access' };
    }

    if (accessState.status === 'refresh-required' || accessState.status === 'expired') {
      if (accessState.canRefresh) {
        try {
          const refreshed = await this.refreshAccessToken(accessState.token);
          return { token: refreshed.token, type: 'access' };
        } catch (error) {
          if (isSessionRejected(error)) this.handleLostAccessSession(showAuthOnUnauthorized);
          throw error;
        }
      }

      this.handleLostAccessSession(showAuthOnUnauthorized);
      throw new Error('Access session has expired');
    }

    const guestToken = await this.ensureGuestToken();
    return { token: guestToken.token, type: 'guest' };
  }

  private async requestTokenRefresh(
    url: string,
    headerName: 'x-access-token' | 'x-guest-token',
    currentToken: string,
    tokenType: TokenType
  ): Promise<RefreshedToken> {
    const response = await this.axios.request({
      url,
      method: 'POST',
      headers: { [headerName]: currentToken },
    });

    const result = this.extractToken(response, tokenType);

    if (tokenType === 'access') {
      TokenStorage.setAccessToken(result.token, result.expiration);
      TokenStorage.clearGuestToken();
      this.authRequiredEmitted = false;
    } else {
      TokenStorage.setGuestToken(result.token, result.expiration);
    }

    return result;
  }

  private extractToken(response: any, tokenType: TokenType): RefreshedToken {
    const body = response?.data ?? response;
    const payload = body?.data ?? body;

    const token =
      tokenType === 'access'
        ? (payload?.access_token ?? payload?.accessToken ?? payload?.token ?? payload?.access)
        : (payload?.guest_token ?? payload?.guestToken ?? payload?.token ?? payload?.guest);

    const expiration = Number(
      payload?.expiration ??
        payload?.expires_at ??
        payload?.expiresAt ??
        (tokenType === 'access'
          ? (payload?.access_token_expiration ?? payload?.accessTokenExpiration)
          : (payload?.guest_token_expiration ?? payload?.guestTokenExpiration))
    );

    if (!token) {
      throw new Error(`${tokenType} token refresh response has invalid format`);
    }

    /* TokenStorage умеет взять exp непосредственно из JWT. */
    if (tokenType === 'access') {
      TokenStorage.setAccessToken(token, expiration || null);
      const stored = TokenStorage.getAccessTokenState();
      if (stored.status === 'missing') throw new Error('Refreshed access token was not saved');
      return { token, expiration: stored.expiration };
    }

    TokenStorage.setGuestToken(token, expiration || null);
    const stored = TokenStorage.getGuestTokenState();
    if (stored.status === 'missing') throw new Error('Refreshed guest token was not saved');
    return { token, expiration: stored.expiration };
  }

  private applyTokenHeader(headers: Record<string, any>, tokenData: ValidToken): void {
    delete headers['x-access-token'];
    delete headers['X-Access-Token'];
    delete headers['x-guest-token'];
    delete headers['X-Guest-Token'];

    if (tokenData.type === 'access') {
      headers['x-access-token'] = tokenData.token;
    } else {
      headers['x-guest-token'] = tokenData.token;
    }
  }

  private async fetchNewGuestToken(force = false): Promise<RefreshedToken> {
    if (!import.meta.client) {
      throw new Error(
        'Guest token can be requested only on client side. Do not call token-required guest API during SSR.'
      );
    }

    if (!force) {
      const state = TokenStorage.getGuestTokenState();
      if (state.status === 'valid') {
        return { token: state.token, expiration: state.expiration };
      }
    }

    if (this.guestTokenPromise) {
      return await this.guestTokenPromise;
    }

    this.guestTokenPromise = this.requestNewGuestToken();

    try {
      return await this.guestTokenPromise;
    } finally {
      this.guestTokenPromise = null;
    }
  }

  private async requestNewGuestToken(): Promise<RefreshedToken> {
    const response = await this.axios.request({
      url: `${API_PREFIX}/${SERVICES_PREFIXES.guestToken}`,
      method: 'GET',
      headers: {},
    });

    const result = this.extractToken(response, 'guest');
    TokenStorage.setGuestToken(result.token, result.expiration);

    return result;
  }

  private handleLostAccessSession(showModal = true): void {
    TokenStorage.clearAccessToken();

    if (!showModal || this.authRequiredEmitted || !import.meta.client) return;

    this.authRequiredEmitted = true;
    window.dispatchEvent(new CustomEvent('app:auth-required'));
  }
}
